// @flow

import * as R from "ramda"
import { createSelector } from "reselect"
import type { OutputSelector } from "reselect"

import createAncillaryReducer from "../reducer"
import {
  serviceStateSelector,
  serviceDataSelector,
  serviceApplicableSegmentsSelector,
  servicePriceSelector,
} from "../selectors"
import Price from "common/records/Price"
import { splitsterSelector } from "common/services/utils/splitsterSelector"
import { getJourney } from "booking/services/journey/reducer"
import { passengersSelector } from "booking/services/reservation/selectors/passengersSelectors"
import type { FastTrackSegment } from "../types"

// Consts
import { titles } from "./records/translations"

const fastTrackReducer: $FlowFixMe = createAncillaryReducer("fastTrack")

export default fastTrackReducer

// can"t import State type because of circular import
export const fastTrackSelector: $FlowFixMe = serviceStateSelector("fastTrack")

type FastTrackDataSelector = OutputSelector<$FlowFixMe, *, FastTrackSegment[]>
export const getFastTrackDataSelector: FastTrackDataSelector = serviceDataSelector("fastTrack")

export const fastTrackApplicableSegmentsSelector = (serviceApplicableSegmentsSelector(
  "fastTrack",
): OutputSelector<$FlowFixMe, *, FastTrackSegment[]>)

export const fastTrackScenesSelector: OutputSelector<$FlowFixMe, *, string[]> = createSelector(
  getFastTrackDataSelector,
  getJourney,
  (data: ?(FastTrackSegment[]), journey) => {
    const { flights } = journey.get("_original")
    // Return empty if data not ready
    if (!data || !flights) return []

    // Fetch unique list of applicable Fast Tracks
    const indices: string[] = R.pipe(
      R.zip(flights),
      R.filter(([, segment]) => R.propEq("is_applicable", true, segment)),
      R.map(([flight]) => R.prop("src", flight)),
      R.uniq,
    )(data)
    return indices
  },
)

export const isFastTrackApplicableSelector: OutputSelector<$FlowFixMe, *, boolean> = createSelector(
  fastTrackScenesSelector,
  splitsterSelector,
  (scenes, splitster) => {
    const ab = [
      { splitsterKey: "FAST_TRACK_NCE", key: "NCE" },
      { splitsterKey: "FAST_TRACK_BGY", key: "BGY" },
    ]
      .filter(x => splitster.get(x.splitsterKey).value === "show")
      .map(R.prop("key"))

    // Find intersection between A/B visible and applicable FTs
    const mapped = R.intersection(scenes, ab)
    const isApplicable = mapped.length > 0

    return isApplicable
  },
)

export const fastTrackReservationBillTextSelector = (createSelector(
  fastTrackScenesSelector,
  scenes => {
    // First fast track is the one with high prio (BGY)
    const scene = scenes[0]

    return titles[scene]
  },
): OutputSelector<$FlowFixMe, *, string>)

// Currently it's fixed as free, this could change later on
export const fastTrackPriceSelector: OutputSelector<$FlowFixMe, *, Price> = servicePriceSelector(
  "fastTrack",
)

export const fastTrackAddedSelector: OutputSelector<$FlowFixMe, *, boolean> = createSelector(
  isFastTrackApplicableSelector,
  // Fast track is added whenever it's applicable
  applicable => applicable,
)

export const fastTrackOrderedPriceSelector: OutputSelector<$FlowFixMe, *, Price> = createSelector(
  fastTrackPriceSelector,
  fastTrackAddedSelector,
  (fastTrackPrice, fastTrackAdded) => (fastTrackAdded ? fastTrackPrice : new Price({ amount: 0 })),
)

// Formatted for Save booking
type FormattedFastTrack = {|
  segment_code: string,
  passenger_id: string,
  price: {
    amount: string,
    currency: string,
    base: string,
    service: string,
    service_flat: string,
    merchant: string,
  },
|}
export const fastTrackFormattedSelector = (createSelector(
  fastTrackApplicableSegmentsSelector,
  isFastTrackApplicableSelector,
  passengersSelector,
  (segments, isApplicable, passengers) => {
    if (isApplicable && passengers) {
      const orderedSegments = []

      passengers.map((pap, id) =>
        segments.map(segment =>
          orderedSegments.push({
            segment_code: segment.segment_code,
            passenger_id: id,
            price: {
              amount: segment.price.amount.toString(),
              currency: segment.price.currency.toString(),
              base: segment.price_info.base_price.toString(),
              service: segment.price_info.service_fee.toString(),
              service_flat: segment.price_info.service_fee.toString(),
              merchant: "0",
            },
          }),
        ),
      )

      return orderedSegments
    }

    // If no applicable segments return empty
    return []
  },
): OutputSelector<$FlowFixMe, *, FormattedFastTrack[]>)
